import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { getDisplayName, STATUS_LABEL, TYP_LABEL } from '../lib/kunden-display';
import type { KundeStatus, KundeTyp } from '../lib/api-enums';

interface Kunde {
  id: string;
  kundennummer: string;
  typ: KundeTyp;
  status: KundeStatus;
  firmenname: string | null;
  vorname: string | null;
  nachname: string | null;
  umsatzsteuerId: string | null;
  notizen: string | null;
  createdAt: string;
  updatedAt: string;
}

type TypFilter = 'alle' | KundeTyp;
type StatusFilter = 'alle' | KundeStatus;

export const Route = createFileRoute('/kunden/')({
  beforeLoad: () => requireAccess('/api/v1/kunden'),
  component: KundenPage,
});

function KundenPage() {
  const list = useQuery({
    queryKey: ['kunden'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });

  const [search, setSearch] = useState('');
  const [typFilter, setTypFilter] = useState<TypFilter>('alle');
  // Default `aktiv` per Plan-Doc §7.1; kann auf "alle" gestellt werden, um
  // archivierte/gesperrte Kunden zu sehen.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('aktiv');

  const filtered = useMemo(() => {
    const items = list.data?.items ?? [];
    const s = search.trim().toLowerCase();
    return items.filter((k) => {
      if (typFilter !== 'alle' && k.typ !== typFilter) return false;
      if (statusFilter !== 'alle' && k.status !== statusFilter) return false;
      if (s) {
        const haystack = [
          k.kundennummer,
          getDisplayName(k),
          k.firmenname ?? '',
          k.vorname ?? '',
          k.nachname ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(s)) return false;
      }
      return true;
    });
  }, [list.data, search, typFilter, statusFilter]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Kunden"
        description="Firmen- und Privatkunden. Anzeige nach Typ und Status filterbar."
        actions={
          <Link to="/kunden/neu">
            <Button>+ Neuer Kunde</Button>
          </Link>
        }
      />

      <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm md:grid-cols-[1fr_auto_auto]">
        <div>
          <label className="text-xs font-medium text-slate-600">Suche</label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nummer, Name, Firma…"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Typ</label>
          <Select value={typFilter} onChange={(e) => setTypFilter(e.target.value as TypFilter)}>
            <option value="alle">Alle</option>
            <option value="firma">Firma</option>
            <option value="privat">Privat</option>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Status</label>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="alle">Alle</option>
            <option value="aktiv">Aktiv</option>
            <option value="inaktiv">Inaktiv</option>
            <option value="gesperrt">Gesperrt</option>
            <option value="archiviert">Archiviert</option>
          </Select>
        </div>
      </div>

      {list.isLoading && <div className="text-slate-500">Lade…</div>}
      <ErrorBanner error={list.error} />

      {list.data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-32 px-4 py-2 text-left">Nummer</th>
                <th className="w-20 px-4 py-2 text-left">Typ</th>
                <th className="px-4 py-2 text-left">Anzeigename</th>
                <th className="px-4 py-2 text-left">USt-ID</th>
                <th className="w-28 px-4 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((k) => (
                <KundeRow key={k.id} kunde={k} />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                    {list.data.items.length === 0
                      ? 'Noch keine Kunden angelegt.'
                      : 'Kein Treffer für die aktuelle Filterung.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500">
            {filtered.length} von {list.data.items.length} Kunden
          </div>
        </div>
      )}
    </div>
  );
}

function KundeRow({ kunde }: { kunde: Kunde }) {
  const display = getDisplayName(kunde) || '–';
  return (
    <tr className="text-sm text-slate-800">
      <td className="px-4 py-2 font-mono text-xs text-slate-600">{kunde.kundennummer}</td>
      <td className="px-4 py-2">
        <TypBadge typ={kunde.typ} />
      </td>
      <td className="px-4 py-2 font-medium">
        <Link to="/kunden/$id" params={{ id: kunde.id }} className="text-brand-700 hover:underline">
          {display}
        </Link>
      </td>
      <td className="px-4 py-2 text-slate-600">{kunde.umsatzsteuerId ?? '–'}</td>
      <td className="px-4 py-2">
        <StatusBadge status={kunde.status} />
      </td>
    </tr>
  );
}

function TypBadge({ typ }: { typ: 'firma' | 'privat' }) {
  const klasse =
    typ === 'firma'
      ? 'bg-slate-100 text-slate-700 border-slate-200'
      : 'bg-amber-50 text-amber-800 border-amber-200';
  return (
    <span
      className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-xs font-medium ${klasse}`}
    >
      {TYP_LABEL[typ] ?? typ}
    </span>
  );
}

function StatusBadge({ status }: { status: Kunde['status'] }) {
  const klasse =
    status === 'aktiv'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'inaktiv'
        ? 'bg-slate-100 text-slate-600 border-slate-200'
        : status === 'gesperrt'
          ? 'bg-red-50 text-red-700 border-red-200'
          : 'bg-violet-50 text-violet-700 border-violet-200';
  return (
    <span
      className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-xs font-medium ${klasse}`}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}
